import _ from "lodash";
import React, { useEffect } from "react";
import { useDebounce } from "use-debounce";

import {
  IDeliveryCutoffSetting,
  IDeliveryWeekLeadTimesSetting
} from "src/core/drops/api/drops.api";
import {
  IProductLeadTimeGroup,
  useProductsApi
} from "src/core/products/api/products.api";
import { useLeadTimeDayRow } from "src/core/shared/hooks/useLeadTimeDayRow";
import { Select } from "src/library/components/Forms";
import { Text } from "src/library/components/Text";
import Grid from "src/library/components/Hedron";
import { StaticTable } from "src/library/components/Tables";
import { getLeadTimeDefaultSetting } from "src/core/orders/utils/order-deliveries";

interface LeadTimeSettingProps {
  handleChange: (leadTimes: IDeliveryWeekLeadTimesSetting) => void;
  value: IDeliveryWeekLeadTimesSetting;
  disabled: boolean;
  currentCutoff: string;
}

/** Displays the lead time table matrix on the settings pages */
export const LeadTimeSetting: React.FC<LeadTimeSettingProps> = ({
  disabled, currentCutoff, value, handleChange
}) => {
  const [ initialValue ] = React.useState(value);
  const [ getLeadTimesResponse, getLeadTimes ] = useProductsApi("GET_ALL_LEAD_TIMES");
  const [ currentLeadTimeGroup, setCurrentLeadTimeGroup ] = React.useState<IProductLeadTimeGroup>();
  // Current lead time configuration will contain current setting values.
  // The values are completed to make sure new lead times are added and it
  // up to date with the current user changes.
  const [ currentLeadTimeConfiguration, setCurrentLeadTimeConfiguration ] = React.useState<null | IDeliveryWeekLeadTimesSetting>(value);
  const [ existingLeadTimes, setExistingLeadTimes ] = React.useState<null | IProductLeadTimeGroup[]>(null);
  const [ debouncedCurrentCutoff ] = useDebounce(currentCutoff, 500);

  const leadTimeDayRows = useLeadTimeDayRow({
    currentLeadTimeGroup,
    currentLeadTimeConfiguration,
    disabled,
    defaultCutoff: { time: currentCutoff || "15:00" },
    setCurrentLeadTimeConfiguration: configuration => {
      handleChange(configuration);
    }
  });

  useEffect(() => {
    getLeadTimes(null);
  }, [ getLeadTimes ]);

  useEffect(() => {
    if (getLeadTimesResponse.data) {
      if (!currentLeadTimeGroup && getLeadTimesResponse.data.length) {
        setCurrentLeadTimeGroup(getLeadTimesResponse.data[ 0 ]);
        setExistingLeadTimes(getLeadTimesResponse.data);
        getLeadTimesResponse.data = null;
      }
    }
  }, [
    currentLeadTimeGroup,
    getLeadTimesResponse,
    getLeadTimesResponse.data
  ]);

  const localCompleteLeadTimeConfiguration = (
    leadTimes: IProductLeadTimeGroup[],
    leadTimeConfiguration: null | IDeliveryWeekLeadTimesSetting,
    defaultCutOff?: IDeliveryCutoffSetting,
    override = false
  ): IDeliveryWeekLeadTimesSetting => {
    let completedLeadTimeConfiguration: IDeliveryWeekLeadTimesSetting | null = leadTimeConfiguration;

    if (!completedLeadTimeConfiguration || !completedLeadTimeConfiguration.deliveryWeekLeadTimes) {
      completedLeadTimeConfiguration = { deliveryWeekLeadTimes: {} };
    }

    leadTimes.map(leadTime => {
      const leadTimeString = leadTime.name.toString();
      const leadTimeDetails = getLeadTimeDefaultSetting(leadTime.defaultLeadTimeDays, defaultCutOff);

      if (completedLeadTimeConfiguration) {
        if (!completedLeadTimeConfiguration.deliveryWeekLeadTimes[ leadTimeString ]) {
          completedLeadTimeConfiguration.deliveryWeekLeadTimes[ leadTimeString ] = leadTimeDetails;
        } else if (override) {
          for (const day in leadTimeDetails) {
            if (leadTimeDetails[ day ].cutoffTime === defaultCutOff?.time) {
              leadTimeDetails[ day ].cutoffTime = undefined;
            }
          }
        }
      }
    });

    return completedLeadTimeConfiguration as IDeliveryWeekLeadTimesSetting;
  };

  useEffect(() => {
    if (existingLeadTimes && initialValue && debouncedCurrentCutoff && currentLeadTimeGroup) {
      setCurrentLeadTimeConfiguration(localCompleteLeadTimeConfiguration(existingLeadTimes, initialValue, { time: debouncedCurrentCutoff }, true));
    }
  }, [
    debouncedCurrentCutoff,
    existingLeadTimes,
    getLeadTimesResponse.data,
    initialValue,
    currentLeadTimeGroup
  ]);

  if (!currentLeadTimeGroup) {
    return null;
  }

  return (
    <>
      <Text
        as="p"
        fontSize="h4"
      >
        Select Lead Times for
        {" "}

        <Text
          fontSize="h4"
          weight="bold"
        >
          {currentLeadTimeGroup.name}
        </Text>

        {" "}
        parts
      </Text>

      <Select
        as="select"
        label="Lead Times"
        autoHideLabels
        className="lead-times-select"
        name="lead-times-options"
        value={currentLeadTimeGroup.name}
        handleChange={e => {
          const newLeadTimeGroup = _.find(existingLeadTimes, ltg => ltg.name === e.target.value);

          setCurrentLeadTimeGroup(newLeadTimeGroup);
        }}
        options={_.map(existingLeadTimes, leadtime => ({
          text: leadtime.name,
          value: leadtime.name
        }))}
      />

      <Grid.Bounds>
        <Grid.Box width="100%">
          {leadTimeDayRows && (
            <StaticTable
              headers={[
                "Week day",
                "Cut Off day",
                "Cut off time"
              ]}
              bodyRows={leadTimeDayRows}
            />
          )}
        </Grid.Box>
      </Grid.Bounds>
    </>
  );
};
